import { For, Show, Suspense, createMemo, createResource, createSignal } from "solid-js";
import { SUPPORT_URL } from "@/lib/support";
import { A } from "@solidjs/router";
import { ChevronDownIcon, CoffeeIcon, ExternalLinkIcon, GitPullRequestIcon, PlusIcon, SearchIcon, StarIcon } from "lucide-solid";
import { WIKI as featured } from "@/content/wiki/index.js";
import stats from "@/data/ecosystem-stats.json";
import { Collapsible, CollapsibleTrigger } from "@/components/ui/collapsible";
import { SpotlightBand, SpotlightSlide, SpotlightTile } from "@/components/spotlight";
import PageHero from "@/components/page-hero";
import { buttonVariants } from "@/components/ui/button";
import { useLocale, hardcoded } from "@/context/locale";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import LinkButton from "@/components/link-button";
import { RowButtons } from "@/components/button-group";
import { parseReadme, README_PROSE } from "@/lib/readme";
import { findProject } from "@/pages/portfolio/ProjectPage";
import { COUNTRY_ARTICLES } from "@/pages/toolkit/tools/litessera-lux/learn/country-articles";
import CountryCard from "@/pages/academia/resources/CountryCard";

const COLLAPSE_AFTER = 12;
const PREVIEW = 6;
const ENTRY_GRID = "grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3";
const README_JSON = import.meta.glob("/src/data/readmes/*.json", { import: "default" });

const ENTRY = /^\s*[-*]\s+\[([^\]]+)\]\(([^)\s]+)\)\s*(.*)$/;
const TAG = /\s*\*\(([^)]+)\)\*\s*$/;

function plain(s) {
  return s.replace(/\*\*?([^*]+)\*\*?/g, "$1").replace(/`([^`]+)`/g, "$1").trim();
}

function hostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function parseList(md) {
  const sections = [];
  let sec = null;
  let sub = null;
  for (const line of md.split("\n")) {
    const h2 = line.match(/^##\s+(.+)$/);
    if (h2) {
      sec = { title: plain(h2[1]), subs: [], body: [] };
      sections.push(sec);
      sub = null;
      continue;
    }
    if (!sec) continue;
    const h3 = line.match(/^###\s+(.+)$/);
    if (h3) {
      sub = { title: plain(h3[1]), entries: [] };
      sec.subs.push(sub);
      continue;
    }
    sec.body.push(line);
    const m = sub && line.match(ENTRY);
    if (!m) continue;
    let rest = m[3].replace(/^[-–—:]\s*/, "");
    const tag = rest.match(TAG);
    if (tag) rest = rest.replace(TAG, "");
    sub.entries.push({ name: plain(m[1]), url: m[2], host: hostOf(m[2]), desc: plain(rest), tag: tag?.[1] ?? "" });
  }

  const lists = sections
    .filter((s) => s.subs.some((x) => x.entries.length))
    .map((s, i) => ({ ...s, id: `section-${i}`, subs: s.subs.filter((x) => x.entries.length) }));
  const notes = sections
    .filter((s) => !s.subs.length && !s.body.some((l) => /\]\(#/.test(l) || /\/portfolio\/awesome-riscv-core/.test(l)))
    .map((s) => ({ title: s.title, html: parseReadme(s.body.join("\n")) }));
  return { lists, notes };
}

function loadList([slug, locale]) {
  const loader = README_JSON[`/src/data/readmes/${slug}.json`];
  if (!loader) return Promise.resolve({ lists: [], notes: [] });
  return loader().then((doc) => parseList(doc[`readme_${locale}`] || doc.readme));
}

function EntryCard(props) {
  return (
    <a
      href={props.entry.url}
      target="_blank"
      rel="noopener"
      class="group flex flex-col gap-1.5 border border-border bg-surface/30 p-4 hover:bg-accent/40 transition-colors min-w-0"
    >
      <div class="flex items-start justify-between gap-3">
        <span class="font-semibold text-foreground leading-snug">{props.entry.name}</span>
        <ExternalLinkIcon size={14} class="text-muted-foreground group-hover:text-foreground shrink-0 mt-1 transition-colors" />
      </div>
      <span class="font-mono text-xs text-muted-foreground truncate">{props.entry.host}</span>
      <Show when={props.entry.desc}>
        <p class="text-sm text-muted-foreground leading-relaxed line-clamp-3">{props.entry.desc}</p>
      </Show>
      <Show when={props.entry.tag}>
        <Badge variant="outline" class="w-fit mt-1 text-xs text-muted-foreground">{props.entry.tag}</Badge>
      </Show>
    </a>
  );
}

function FeaturedTile(props) {
  const { t, prefix, locale } = useLocale();
  const stars = () => stats.entries[props.item.id]?.stars;
  return (
    <SpotlightTile
      href={`${prefix}/academia/resources/riscv/ecosystem/${props.item.id}`}
      meta={`${t(`awesome.featured.kinds.${props.item.kind}`)} · ${props.item.language}`}
      name={props.item.name}
      stat={stars() != null ? <><StarIcon size={14} />{new Intl.NumberFormat(locale).format(stars())}</> : null}
      openLabel={t("awesome.featured.open")}
      caption={props.item.origin}
      text={t(`awesome.featured.items.${props.item.id}`)}
    />
  );
}

function FeaturedBand() {
  const { t, prefix } = useLocale();
  const requestHref = () => `${prefix}/contact?subject=${encodeURIComponent(t("awesome.featured.request"))}`;
  const lead = featured.filter((e) => e.spotlight);
  const rest = featured.filter((e) => !e.spotlight);
  return (
    <SpotlightBand
      label={t("awesome.featured.title")}
      lead={t("awesome.featured.lead")}
      showArrows={lead.length > 3}
      previousLabel={t("awesome.featured.previous")}
      nextLabel={t("awesome.featured.next")}
      action={
        <LinkButton href={requestHref()}>{t("awesome.featured.request")}</LinkButton>
      }
      after={<FeaturedIndex items={rest} requestHref={requestHref()} />}
    >
      <For each={lead}>{(item) => <SpotlightSlide><FeaturedTile item={item} /></SpotlightSlide>}</For>
    </SpotlightBand>
  );
}

function FeaturedIndex(props) {
  const { t, prefix } = useLocale();
  return (
    <div class="grid grid-cols-2 sm:grid-cols-[repeat(auto-fit,minmax(180px,1fr))] border-t border-l border-border">
      <For each={props.items}>
        {(item) => (
          <A
            href={`${prefix}/academia/resources/riscv/ecosystem/${item.id}`}
            class="flex flex-col items-center justify-center gap-1.5 px-4 py-8 border-r border-b border-border text-center hover:bg-surface transition-colors min-w-0"
          >
            <span class="text-xl md:text-2xl font-bold tracking-[0.08em] uppercase text-foreground break-words">{item.name}</span>
            <span class="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">{t(`awesome.featured.kinds.${item.kind}`)}</span>
          </A>
        )}
      </For>
      <a
        href={props.requestHref}
        class="flex flex-col items-center justify-center gap-2 px-4 py-8 border-r border-b border-border text-center hover:bg-surface transition-colors min-w-0"
      >
        <span class="flex h-9 w-9 items-center justify-center rounded-full border border-dashed border-purple text-purple">
          <PlusIcon size={16} />
        </span>
        <span class="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted-foreground">{t("awesome.featured.request")}</span>
      </a>
    </div>
  );
}

function Subsection(props) {
  const { t } = useLocale();
  const [open, setOpen] = createSignal(false);
  const collapsible = () => props.sub.entries.length > COLLAPSE_AFTER;
  const expanded = () => props.searching || open();
  const head = () => (collapsible() ? props.sub.entries.slice(0, PREVIEW) : props.sub.entries);
  const tail = () => (collapsible() ? props.sub.entries.slice(PREVIEW) : []);

  return (
    <Collapsible open={expanded()} onOpenChange={setOpen} class="flex flex-col gap-3">
      <h3 class="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{props.sub.title}</h3>
      <div class={ENTRY_GRID}>
        <For each={head()}>{(entry) => <EntryCard entry={entry} />}</For>
      </div>
      <div class={ENTRY_GRID} classList={{ hidden: !expanded() }}>
        <For each={tail()}>{(entry) => <EntryCard entry={entry} />}</For>
      </div>
      <Show when={collapsible() && !props.searching}>
        <CollapsibleTrigger class={`${buttonVariants({ variant: "outline" })} w-full justify-center py-3 [&[data-expanded]>svg]:rotate-180`}>
          {open() ? t("awesome.showLess") : t("awesome.showAll")}
          <ChevronDownIcon size={16} class="shrink-0 transition-transform duration-200" />
        </CollapsibleTrigger>
      </Show>
    </Collapsible>
  );
}

export default function AwesomeListPage(props) {
  const { t, prefix, locale } = useLocale();
  const project = createMemo(() => findProject(props.slug));
  const [data] = createResource(() => [props.slug, locale], loadList, { initialValue: { lists: [], notes: [] } });

  const [query, setQuery] = createSignal("");
  const q = () => query().trim().toLowerCase();
  const filtered = createMemo(() => {
    const needle = q();
    const lists = data().lists;
    if (!needle) return lists;
    return lists
      .map((s) => ({
        ...s,
        subs: s.subs
          .map((x) => ({ ...x, entries: x.entries.filter((e) => `${e.name} ${e.desc} ${e.host}`.toLowerCase().includes(needle)) }))
          .filter((x) => x.entries.length),
      }))
      .filter((s) => s.subs.length);
  });

  return (
    <section class="min-h-screen bg-background">
      <PageHero
        eyebrow={t("awesome.eyebrow")}
        title={t("nav.awesome")}
        description={t("awesome.lead").replace("{name}", hardcoded("Awesome RISC-V"))}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href={prefix || "/"}>{t("breadcrumb.home")}</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbLink href={`${prefix}/portfolio`}>{t("breadcrumb.portfolio")}</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbLink current>{t("nav.awesome")}</BreadcrumbLink></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
      >
        <RowButtons>
          <LinkButton href="#section-0" icon={<ChevronDownIcon size={15} />}>
            {t("awesome.browse")}
          </LinkButton>
          <LinkButton href={project()?.url} external icon={<StarIcon size={15} />}>
            {t("awesome.star")}
          </LinkButton>
          <LinkButton href={`${project()?.url}/pulls`} external icon={<GitPullRequestIcon size={15} />}>
            {t("awesome.suggest")}
          </LinkButton>
          <LinkButton href={SUPPORT_URL} external icon={<CoffeeIcon size={15} />}>
            {t("footer.support")}
          </LinkButton>
        </RowButtons>
      </PageHero>

      <FeaturedBand />

      <Show when={COUNTRY_ARTICLES.length}>
        <section class="px-[5%] py-16 md:py-24 flex flex-col gap-6">
          <h2 class="text-sm font-semibold tracking-widest uppercase text-muted-foreground">
            {t("toolkit.venus.learn.countries")}
          </h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-6xl w-full">
            <For each={COUNTRY_ARTICLES}>
              {(lesson) => (
                <CountryCard lesson={lesson} href={`${prefix}/academia/resources/riscv/learn/${lesson.id}`} />
              )}
            </For>
          </div>
        </section>
      </Show>

      <div class="flex flex-col md:flex-row border-t border-border bg-[var(--band)]">
        <nav class="md:w-64 shrink-0 border-b md:border-b-0 md:border-r border-border p-4 flex md:flex-col gap-1 overflow-x-auto md:sticky md:top-[var(--nav-height,0px)] md:h-[calc(100vh-var(--nav-height,0px))] md:overflow-y-auto">
          <span class="px-3 py-2 text-xs font-semibold tracking-widest uppercase text-muted-foreground whitespace-nowrap">{t("awesome.sections")}</span>
          <For each={data().lists}>
            {(s) => (
              <a
                href={`#${s.id}`}
                class="px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground whitespace-nowrap transition-colors"
              >
                {s.title}
              </a>
            )}
          </For>
        </nav>

        <div class="flex-1 min-w-0 px-[5%] py-16 md:py-20 flex flex-col gap-20">
          <div class="sticky top-[var(--nav-height,0px)] z-20 -mx-1 px-1 bg-[var(--band)]">
            <div class="py-2 relative max-w-2xl">
              <SearchIcon size={15} class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                value={query()}
                onInput={(e) => setQuery(e.currentTarget.value)}
                placeholder={t("awesome.search")}
                aria-label={t("awesome.search")}
                class="pl-9"
              />
            </div>
          </div>

          <Suspense fallback={<div class="min-h-40" />}>
            <Show when={q() && !filtered().length}>
              <p class="text-sm text-muted-foreground py-8">{t("awesome.noResults")}</p>
            </Show>

            <div class="flex flex-col gap-16">
              <For each={filtered()}>
                {(s) => (
                  <section id={s.id} class="flex flex-col gap-8 scroll-mt-[calc(var(--nav-height,0px)+5rem)]">
                    <h2 class="text-2xl md:text-3xl font-bold tracking-tight text-foreground border-l-2 border-purple pl-3">{s.title}</h2>
                    <For each={s.subs}>{(sub) => <Subsection sub={sub} searching={!!q()} />}</For>
                  </section>
                )}
              </For>
            </div>

            <div class="flex flex-col gap-8 pt-10 border-t border-border">
              <For each={data().notes}>
                {(note) => (
                  <div class="flex flex-col gap-2 max-w-3xl">
                    <h2 class="text-lg font-bold text-foreground">{note.title}</h2>
                    <div
                      class={`${README_PROSE} text-sm text-muted-foreground`}
                      // eslint-disable-next-line solid/no-innerhtml
                      innerHTML={note.html}
                    />
                  </div>
                )}
              </For>
            </div>
          </Suspense>
        </div>
      </div>
    </section>
  );
}
